import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaButtonGroup } from "@/shared/components/buttons/ColocaButtonGroup";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import type { OrganizationsTranslations } from "@/shared/lang/interfaces/organizations";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { ORGANIZATION_FEATURE_CATALOG } from "../constants/organizationsAdmin.constants";
import type { GlobalFeature, GlobalFeatureMode } from "../types/organizationsAdmin.types";

type CopyKey = keyof OrganizationsTranslations;

interface RolloutCopy {
  /** The state line while on for all. */
  bodyOn: CopyKey;
  /** The state line while each organization decides. */
  bodyPerOrganization: CopyKey;
  groupAria: CopyKey;
  confirmOnTitle: CopyKey;
  confirmOnBody: CopyKey;
  /** Leaving on for all: who keeps it, and what in flight finishes. */
  confirmPerOrganizationBody: CopyKey;
  /** Off for all: what stops, and what it leaves running. */
  confirmOffBody: CopyKey;
}

const ROLLOUT_COPY: Record<GlobalFeature, RolloutCopy> = {
  payout_batches_enabled: {
    bodyOn: "payout_batches_rollout_body_on",
    bodyPerOrganization: "payout_batches_rollout_body_off",
    groupAria: "payout_batches_rollout_switch_aria",
    confirmOnTitle: "payout_batches_rollout_confirm_on_title",
    confirmOnBody: "payout_batches_rollout_confirm_on_body",
    confirmPerOrganizationBody: "payout_batches_rollout_confirm_off_body",
    confirmOffBody: "payout_batches_rollout_confirm_block_body",
  },
  breb_receiving_key_enabled: {
    bodyOn: "rollout_body_on",
    bodyPerOrganization: "rollout_body_off",
    groupAria: "rollout_switch_aria",
    confirmOnTitle: "rollout_confirm_on_title",
    confirmOnBody: "rollout_confirm_on_body",
    confirmPerOrganizationBody: "rollout_confirm_off_body",
    confirmOffBody: "rollout_confirm_block_body",
  },
  otc_enabled: {
    bodyOn: "crypto_rollout_body_on",
    bodyPerOrganization: "crypto_rollout_body_off",
    groupAria: "crypto_rollout_switch_aria",
    confirmOnTitle: "crypto_rollout_confirm_on_title",
    confirmOnBody: "crypto_rollout_confirm_on_body",
    confirmPerOrganizationBody: "crypto_rollout_confirm_off_body",
    confirmOffBody: "crypto_rollout_confirm_block_body",
  },
  card_deposits_enabled: {
    bodyOn: "card_deposits_rollout_body_on",
    bodyPerOrganization: "card_deposits_rollout_body_off",
    groupAria: "card_deposits_rollout_switch_aria",
    confirmOnTitle: "card_deposits_rollout_confirm_on_title",
    confirmOnBody: "card_deposits_rollout_confirm_on_body",
    confirmPerOrganizationBody: "card_deposits_rollout_confirm_off_body",
    confirmOffBody: "card_deposits_rollout_confirm_block_body",
  },
  card_cop_top_ups_enabled: {
    bodyOn: "card_cop_top_ups_rollout_body_on",
    bodyPerOrganization: "card_cop_top_ups_rollout_body_off",
    groupAria: "card_cop_top_ups_rollout_switch_aria",
    confirmOnTitle: "card_cop_top_ups_rollout_confirm_on_title",
    confirmOnBody: "card_cop_top_ups_rollout_confirm_on_body",
    confirmPerOrganizationBody: "card_cop_top_ups_rollout_confirm_off_body",
    confirmOffBody: "card_cop_top_ups_rollout_confirm_block_body",
  },
  breb_qr_payments_enabled: {
    bodyOn: "breb_qr_payments_rollout_body_on",
    bodyPerOrganization: "breb_qr_payments_rollout_body_off",
    groupAria: "breb_qr_payments_rollout_switch_aria",
    confirmOnTitle: "breb_qr_payments_rollout_confirm_on_title",
    confirmOnBody: "breb_qr_payments_rollout_confirm_on_body",
    confirmPerOrganizationBody: "breb_qr_payments_rollout_confirm_off_body",
    confirmOffBody: "breb_qr_payments_rollout_confirm_block_body",
  },
};

const MODE_LABEL: Record<GlobalFeatureMode, CopyKey> = {
  per_organization: "rollout_mode_per_organization",
  on_for_all: "rollout_mode_on_for_all",
  off_for_all: "rollout_mode_off_for_all",
};

const MODE_ORDER: readonly GlobalFeatureMode[] = ["per_organization", "on_for_all", "off_for_all"];

export interface FeatureRolloutCardProps {
  feature: GlobalFeature;
  /** Null while the first read is in flight. */
  mode: GlobalFeatureMode | null;
  isSaving: boolean;
  /** Another global switch is being written: this one waits for it. */
  isLocked?: boolean;
  /** Omitted for readers without `organizations:write`: the control renders read-only. */
  onChange?: (mode: GlobalFeatureMode) => Promise<void>;
}

// Every move is confirmed: each one changes what every organization has at once.
export function FeatureRolloutCard({
  feature,
  mode,
  isSaving,
  isLocked = false,
  onChange,
}: FeatureRolloutCardProps) {
  const { t } = useTranslations("organizations");
  const copy = ROLLOUT_COPY[feature];
  const definition = ORGANIZATION_FEATURE_CATALOG.find((entry) => entry.global === feature);
  const Icon = definition?.icon;
  const name = definition ? t(definition.labelKey) : "";
  const blocked = mode === "off_for_all";
  const [confirming, setConfirming] = useState(false);
  // Kept after closing, together with the mode it left, so the dialog does not change its copy while it animates out.
  const [move, setMove] = useState<{ from: GlobalFeatureMode; to: GlobalFeatureMode }>({
    from: "per_organization",
    to: "on_for_all",
  });

  const confirmTitle = {
    on_for_all: t(copy.confirmOnTitle),
    per_organization: t("rollout_confirm_per_organization_title", { feature: name }),
    off_for_all: t("rollout_confirm_off_for_all_title", { feature: name }),
  }[move.to];
  const confirmBody = {
    on_for_all: t(copy.confirmOnBody),
    per_organization:
      move.from === "off_for_all" ? t("rollout_confirm_unblock_body") : t(copy.confirmPerOrganizationBody),
    off_for_all: `${t(copy.confirmOffBody)} ${t("rollout_confirm_off_for_all_keeps")}`,
  }[move.to];
  const confirmLabel = {
    on_for_all: t("rollout_confirm_on_cta"),
    per_organization: t("rollout_confirm_per_organization_cta"),
    off_for_all: t("rollout_confirm_off_for_all_cta"),
  }[move.to];
  // Opening to all or lifting a block gives access back; the other two take it away from organizations.
  const confirmVariant =
    move.to === "off_for_all" || (move.to === "per_organization" && move.from === "on_for_all")
      ? "destructive"
      : "primary";

  return (
    <ColocaSurface padding="md" className={cn(blocked && "border-destructive/40")}>
      <div className="flex items-start gap-3">
        {Icon && (
          <div
            className={cn(
              "flex size-9 shrink-0 items-center justify-center rounded-full text-white",
              blocked ? "bg-destructive" : "bg-[#00735C]",
            )}
          >
            <Icon className="size-4" aria-hidden="true" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          {definition && <p className="text-sm font-semibold text-foreground">{name}</p>}
          {definition && (
            <p className="mt-0.5 text-sm text-muted-foreground">{t(definition.descriptionKey)}</p>
          )}
          {mode === null ? (
            <ColocaSkeleton className="mt-2 h-4 w-64 max-w-full" />
          ) : (
            <p
              className={cn(
                "mt-2 flex items-start gap-1.5 text-xs",
                mode === "per_organization" && "text-muted-foreground",
                mode === "on_for_all" && "text-foreground",
                blocked && "font-medium text-destructive",
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "mt-1 size-1.5 shrink-0 rounded-full",
                  mode === "per_organization" && "bg-muted-foreground/50",
                  mode === "on_for_all" && "bg-success",
                  blocked && "bg-destructive",
                )}
              />
              {
                {
                  per_organization: t(copy.bodyPerOrganization),
                  on_for_all: t(copy.bodyOn),
                  off_for_all: t("rollout_body_off_for_all"),
                }[mode]
              }
            </p>
          )}
        </div>
      </div>
      {/* Under the text from `sm` up; the card's full width below, where the three labels need it. */}
      <ColocaButtonGroup aria-label={t(copy.groupAria)} className="mt-3 max-w-full flex-wrap sm:ml-12">
        {MODE_ORDER.map((option) => {
          const selected = option === mode;
          return (
            <ColocaButton
              key={option}
              size="sm"
              variant={selected ? (option === "off_for_all" ? "destructive" : "secondary") : "default"}
              aria-pressed={selected}
              className={selected ? "font-semibold" : "text-muted-foreground"}
              disabled={mode === null || !onChange || isSaving || isLocked}
              onClick={() => {
                if (selected || mode === null) return;
                setMove({ from: mode, to: option });
                setConfirming(true);
              }}
            >
              {t(MODE_LABEL[option])}
            </ColocaButton>
          );
        })}
      </ColocaButtonGroup>
      <ColocaAlertDialog
        open={confirming}
        onOpenChange={setConfirming}
        media={Icon ? <Icon className="size-8" /> : undefined}
        title={confirmTitle}
        description={confirmBody}
        confirmLabel={confirmLabel}
        confirmVariant={confirmVariant}
        confirmDisabled={isSaving || isLocked}
        cancelLabel={t("rollout_cancel")}
        onConfirm={() => {
          if (!onChange) return;
          void onChange(move.to).finally(() => setConfirming(false));
        }}
      />
    </ColocaSurface>
  );
}
